import { FC, useState, useEffect, useRef } from "react";
import { ChevronDown, X } from "lucide-react";

type OptionId = string | number;

type Option = {
  id: OptionId;
  label: string | number | null;
};

interface MultiSelectDropdownProps {
  label?: string;
  options?: Option[];
  selectedValues?: OptionId[];
  onChange?: (next: OptionId[]) => void;
  hasActiveFilter?: boolean;
  onClear?: () => void;
  placeholder?: string;
}

// Multi-select dropdown component with checkboxes and search
const MultiSelectDropdown: FC<MultiSelectDropdownProps> = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  hasActiveFilter = false,
  onClear,
  placeholder = 'Todos',
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(opt =>
    opt.label && opt.label.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (optionId: OptionId) => {
    const newSelected = selectedValues.includes(optionId)
      ? selectedValues.filter(id => id !== optionId)
      : [...selectedValues, optionId];
    onChange?.(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      // Desmarcar todos (apenas os filtrados)
      const filteredIds = filteredOptions.map(opt => opt.id);
      onChange?.(selectedValues.filter(id => !filteredIds.includes(id)));
    } else {
      // Marcar todos os filtrados
      const allFilteredIds = filteredOptions.map(opt => opt.id);
      const newSelected = [...new Set([...selectedValues, ...allFilteredIds])];
      onChange?.(newSelected);
    }
  };

  const selectedCount = selectedValues.length;
  const displayText = selectedCount === 0
    ? placeholder
    : selectedCount === 1
    ? options.find(opt => opt.id === selectedValues[0])?.label || '1 selecionado'
    : `${selectedCount} selecionados`;

  const allFilteredSelected = filteredOptions.length > 0 &&
    filteredOptions.every(opt => selectedValues.includes(opt.id));

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onClear) {
      onClear();
    } else {
      onChange?.([]);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <label style={{ fontSize: '12px', fontWeight: '600' }}>
          {label}
        </label>
        {selectedCount > 0 && (
          <button
            onClick={handleClear}
            style={{
              background: 'none',
              border: 'none',
              padding: '2px 6px',
              cursor: 'pointer',
              fontSize: '11px',
              color: 'var(--muted)',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'var(--muted)'; }}
          >
            <X size={12} />
            Limpar
          </button>
        )}
      </div>
      {/* P3-017: semantic <button> with aria-expanded so the trigger is
          keyboard-reachable and screen-readers announce open state. */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label}
        className={hasActiveFilter ? 'multiselect-dropdown--has-filter' : ''}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '8px 12px',
          border: hasActiveFilter ? '2px solid transparent' : '1px solid var(--panel-border)',
          borderRadius: '6px',
          background: 'white',
          cursor: 'pointer',
          minHeight: '36px',
          font: 'inherit',
          textAlign: 'left',
          color: 'inherit',
        }}
      >
        <span style={{ fontSize: '14px' }}>{displayText}</span>
        <ChevronDown size={16} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          background: 'white',
          border: '1px solid var(--panel-border)',
          borderRadius: '4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          maxHeight: '300px',
          zIndex: 1000
        }}>
          {/* Search input */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid var(--panel-border)',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Select All / Deselect All */}
          <div
            onClick={toggleSelectAll}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              background: '#f9fafb',
              borderBottom: '2px solid var(--panel-border)',
              fontWeight: '600'
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
              e.currentTarget.style.background = '#f9fafb';
            }}
          >
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={() => {}}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px' }}>
              {allFilteredSelected ? 'Desmarcar todos' : 'Selecionar todos'}
            </span>
          </div>

          {/* Options list */}
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filteredOptions.map(option => (
              <div
                key={option.id}
                onClick={() => toggleOption(option.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.id)}
                  onChange={() => {}}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>{option.label}</span>
              </div>
            ))}
          </div>

          {filteredOptions.length === 0 && (
            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--muted)', fontSize: '14px' }}>
              Nenhum resultado encontrado
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
